import { motion } from 'framer-motion';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '@/lib/biomarkers';

interface BiomarkerSelectorProps {
  availableBiomarkers: Array<{ key: string; name: string; category: string }>;
  selected: string | null;
  onSelect: (key: string | null) => void;
}

/**
 * Group biomarkers by category, following CATEGORY_ORDER.
 */
function groupByCategory(
  biomarkers: Array<{ key: string; name: string; category: string }>,
): Array<{ category: string; label: string; markers: Array<{ key: string; name: string }> }> {
  const groups = new Map<string, Array<{ key: string; name: string }>>();

  for (const bm of biomarkers) {
    const cat = bm.category || 'other';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push({ key: bm.key, name: bm.name });
  }

  const result: Array<{ category: string; label: string; markers: Array<{ key: string; name: string }> }> = [];

  for (const cat of CATEGORY_ORDER) {
    const marks = groups.get(cat);
    if (marks && marks.length > 0) {
      result.push({
        category: cat,
        label: CATEGORY_LABELS[cat] || cat,
        markers: marks,
      });
      groups.delete(cat);
    }
  }

  // Remaining categories not in CATEGORY_ORDER
  for (const [cat, marks] of groups) {
    result.push({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1),
      markers: marks,
    });
  }

  return result;
}

export function BiomarkerSelector({ availableBiomarkers, selected, onSelect }: BiomarkerSelectorProps) {
  const grouped = groupByCategory(availableBiomarkers);

  if (availableBiomarkers.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-navy">Biomarker Trends</h2>
        {selected && (
          <button
            onClick={() => onSelect(null)}
            className="text-xs font-medium text-copper hover:text-copper-dark transition-colors"
          >
            Clear selection
          </button>
        )}
      </div>

      <div className="space-y-2">
        {grouped.map((group) => (
          <div key={group.category}>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-gray-400">
              {group.label}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {group.markers.map((marker) => {
                const isSelected = selected === marker.key;
                return (
                  <motion.button
                    key={marker.key}
                    onClick={() => onSelect(isSelected ? null : marker.key)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-copper text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    whileTap={{ scale: 0.95 }}
                  >
                    {marker.name}
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
